import type { LocalDate } from "@js-joda/core";
import cn from "classnames";
import { type FC, useCallback, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
	canApprove,
	isSocialPlatform,
	type PostDto,
	PostStatus,
	postContraintCheck,
	postLabel,
} from "shared";
import { Icon } from "../common/Icon";
import { useModal } from "../common/modal/Modal";
import { PlatformIcon } from "../common/PlatformIcon";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { TimeFormatter, useTime } from "../common/time/useTime";
import { useCompany } from "../company/useCompany";
import {
	useSuspenseMedia,
	useSuspenseMediaList,
} from "../content/useMediaBatch";
import { LinkGen } from "../LinkGen";
import { ModalPostPreview } from "../preview/ModalPostPreview";
import { SocialMediaPost } from "../social-media/SocialMediaPost";
import { useCalendar } from "./CalendarContext";
import { getPostDate, getWeekdays } from "./calendar.utils";
import {
	usePostsActions,
	useSuspensePosts,
	useSuspensePostsListRange,
} from "./usePosts";

type CalendarDaysProps = {
	days: Array<LocalDate>;
	getPosts: (date: LocalDate) => Array<PostDto>;
	onApprove: (postId: number) => void;
	canApprovePost: boolean;
	requirePostApproval: boolean;
};

type CalendarWeekDaysProps = {
	days: Array<LocalDate>;
	getPosts: (date: LocalDate) => Array<PostDto>;
};

const CalendarDaysView: FC<Readonly<CalendarDaysProps>> = ({
	days,
	getPosts,
	onApprove,
	canApprovePost,
	requirePostApproval,
}) => {
	const { format } = useTime();

	return days.map((x) => (
		<div className={cn("calendar-day")} key={x.toString()}>
			<div className="mb-1">{format(TimeFormatter.Date, x)}</div>
			<div className="calendar-post-list">
				{getPosts(x).map((post) => (
					<SocialMediaPost
						post={post}
						key={post.id}
						onApprove={onApprove}
						canApprove={canApprovePost}
						requirePostApproval={requirePostApproval}
					/>
				))}
			</div>
		</div>
	));
};

const CalendarWeekView: FC<
	Readonly<
		CalendarWeekDaysProps & {
			onPreview: (post: PostDto) => void;
			onApprove: (postId: number) => void;
			canApprovePost: boolean;
			requirePostApproval: boolean;
		}
	>
> = ({
	days,
	getPosts,
	onPreview,
	onApprove,
	canApprovePost,
	requirePostApproval,
}) => {
	return (
		<>
			{days.map((day) => (
				<div key={`week-day-${day.toString()}`} className="calendar-col">
					{getPosts(day).map((post) => {
						const { valid, errorMessage } = postContraintCheck(
							post.platform,
							post.content,
							post.files,
							undefined,
							{ status: post.status, refId: post.refId },
						);
						const showPreview = isSocialPlatform(post.platform);
						const showApprove =
							canApprovePost &&
							(post.status === PostStatus.PENDING_APPROVAL ||
								(requirePostApproval && post.status === PostStatus.DRAFT));
						return (
							<div className="calendar-post-wrapper" key={post.id}>
								<Link className="calendar-post" to={LinkGen.Post(post.id)}>
									<span className="d-block">
										<PlatformIcon value={post.platform} />
									</span>

									<span className="calendar-post-content">
										{!valid && (
											<span>
												<SimpleTooltip
													tooltip={errorMessage}
													placement="bottom"
												>
													<span className="circle" />
												</SimpleTooltip>
											</span>
										)}
										{post.status === PostStatus.PENDING_APPROVAL && (
											<SimpleTooltip
												tooltip="Pending approval"
												placement="bottom"
											>
												<span className="circle circle-warning" />
											</SimpleTooltip>
										)}

										{postLabel(post)}
									</span>
								</Link>
								{showApprove && (
									<button
										type="button"
										className="calendar-post-approve-btn"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											onApprove(post.id);
										}}
									>
										<SimpleTooltip tooltip="Approve" placement="top">
											<Icon name="check_circle" size={16} />
										</SimpleTooltip>
									</button>
								)}
								{showPreview && (
									<button
										type="button"
										className="calendar-post-preview-btn"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											onPreview(post);
										}}
									>
										<Icon name="visibility" size={16} />
									</button>
								)}
							</div>
						);
					})}
				</div>
			))}
		</>
	);
};

export const CalendarGrid = () => {
	const { days, type: calendarType, platformFilter } = useCalendar();
	const { IDs } = useSuspensePostsListRange(days[0], days[days.length - 1]);
	const posts = useSuspensePosts(IDs);
	const weekdays = useMemo(() => getWeekdays(), []);
	const weekDaysRef = useRef<HTMLDivElement | null>(null);
	const { open } = useModal();
	const mediaIds = useSuspenseMediaList();
	const media = useSuspenseMedia(mediaIds);
	const company = useCompany();
	const { approvePost } = usePostsActions();

	const canApprovePost = company.myRole ? canApprove(company.myRole) : false;

	const getPosts = useCallback(
		(date: LocalDate) => {
			return posts.filter((post) => {
				// TODO: Check how LocalDate -> Instant conversion works on different timezones
				const postDate = getPostDate(post).toLocalDate();
				if (!postDate.isEqual(date)) {
					return false;
				}
				if (platformFilter && !platformFilter.has(post.platform)) {
					return false;
				}
				return true;
			});
		},
		[posts, platformFilter],
	);

	const handlePreview = useCallback(
		(post: PostDto) => {
			const files = media.filter((f) => post.files?.includes(f.id));
			const thumbnailFile = post.youtubeThumbnailFileId
				? media.find((f) => f.id === post.youtubeThumbnailFileId)
				: undefined;
			open(ModalPostPreview, {
				content: post.content,
				files,
				initialPlatform: post.platform,
				thumbnailFile,
			});
		},
		[open, media],
	);

	const handleApprove = useCallback(
		(postId: number) => {
			approvePost.mutate(postId);
		},
		[approvePost],
	);

	useEffect(() => {
		const calcTop = () => {
			if (calendarType === "week" && weekDaysRef.current) {
				const { top } = weekDaysRef.current.getBoundingClientRect();
				const documentTop = top + window.scrollY;
				weekDaysRef.current.style.setProperty(
					"--offset-top",
					`${documentTop}px`,
				);
			}
		};

		calcTop();
		window.addEventListener("resize", calcTop);

		return () => {
			window.removeEventListener("resize", calcTop);
		};
	}, [calendarType]);

	return (
		<div className="calendar-scroll">
			<div className="calendar">
				<div className="calendar-days">
					{calendarType === "month"
						? weekdays.map((weekday) => (
								<div key={`day-${weekday.name()}`}>
									{weekday.name().substring(0, 3)}
								</div>
							))
						: days.map((weekday) => (
								<div key={`weekday-${weekday.dayOfWeek().name()}`}>
									{weekday.dayOfWeek().name()} {weekday.dayOfMonth()}
								</div>
							))}
				</div>
				{calendarType === "month" ? (
					<div className="calendar-week">
						<CalendarDaysView
							getPosts={getPosts}
							days={days}
							onApprove={handleApprove}
							canApprovePost={canApprovePost}
							requirePostApproval={company.requirePostApproval}
						/>
					</div>
				) : (
					<div ref={weekDaysRef} className="calendar-week-days">
						<CalendarWeekView
							getPosts={getPosts}
							days={days}
							onPreview={handlePreview}
							onApprove={handleApprove}
							canApprovePost={canApprovePost}
							requirePostApproval={company.requirePostApproval}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
